/**
 * 충전금 > 출금 (/m/account/exchange)
 *
 * 금액칸 아래 안내줄(#cxHint)이 '왜 버튼이 안 눌리는지'를 입력하는 즉시 말한다 — 누르고 나서
 * 알림창으로 알리지 않는다. 신청·취소 뒤에는 새로고침 없이 잔액 숫자와 내역만 다시 그린다
 * (출금신청중 금액은 홀드에 더해지고 출금가능에서 빠진다 — AccountController::cashExchange 의 계산과 같다).
 */
import Util from '../../Common/Util';
import Form from '../../Common/Form';

const MIN_AMOUNT = 10000;   // 이 미만은 전액 출금일 때만 받는다
const PAGE_SIZE = 10;
// 운영시간 안내를 접어 뒀는지 ('1' = 접음). 기기 취향이라 회원번호를 붙이지 않는다 (.ai/state-management.md §4-1)
const LS_NOTICE_FOLDED = 'm:cash:exchangeNoticeFolded';

// g5_cash_request.status (출금) → [뱃지 클래스, 문구]
const STATUS = {
    20: ['is-ready', '출금신청'],
    21: ['is-wait', '출금대기'],
    22: ['is-done', '출금완료'],
    23: ['is-cancel', '출금취소'],
};

export default class AccountExchange {
    initialize() {
        // 본인인증 생략(개발 서버) — F/Inicis 가 G5['inicisByPass'] 를 본다 (#page-data)
        G5['inicisByPass'] = JSON.parse(document.getElementById('page-data')?.textContent || '{}').inicisByPass;
        // 이니시스 인증 콜백 — 콜백 페이지(plugin/inicis/callback)가 opener 의 이 이름을 부른다 (창 사이 약속이라 window)
        window.inicisCallback = (data) => this.onInicisCallback(data);

        this.$root = $('#cxRoot');
        if (!this.$root.length) return;

        const d = this.$root.data();
        this.usable = Util.int(d.usable);
        this.total = Util.int(d.total);
        this.hold = Util.int(d.hold);
        this.hasBank = d.hasBank == 1;
        this.isPenalty = d.penalty == 1;

        this.rows = [];
        this.monthSums = {};
        this.recordsTotal = 0;
        this.busy = false;
        this.loadingHist = false;

        this.$hist = $('#cxHist');
        this.$more = $('#cxMore');
        this.$more.on('click', () => this.loadHistory());
        this.$hist.on('click', '.cx-cancel', (e) => this.onClickCancel($(e.currentTarget).data('num')));

        // 무한 스크롤 — '더 보기' 자리가 화면에 들어오면 다음 쪽을 자동으로 붙인다.
        // attach 가 null 이면(IntersectionObserver 미지원) 버튼이 그대로 폴백으로 남는다.
        if ($j.Ui.InfiniteScroll.attach(this.$more[0], { onLoad: () => this.loadHistory() })) {
            this.$more.addClass('is-auto');
        }

        // 출금금지 회원은 신청영역이 렌더되지 않는다 — 내역만 보여준다
        if (d.ban != 1) this._initRequest();

        this.loadHistory(true);
    }

    /* ───────────────── 신청 영역 ───────────────── */

    _initRequest() {
        this.$input = $('#cashoutamt');
        this.$hint = $('#cxHint');
        this.$submit = $('#exchangeButton');

        // 실명인증 여부로 인증 카드 / 신청 카드 중 하나만 연다
        const verified = !!MEMBER['real_name'];
        $('#inicis-auth-area').prop('hidden', verified);
        $('#exchange-request-area').prop('hidden', !verified);
        $('#cxAuthBtn').on('click', () => this.onClickAuth());
        // 출금계좌 변경은 고객센터 통화로, 등록은 서류등록(업체·장비·기사)의 통장사본에서 한다
        $('#cxAcctBtn').on('click', () => {
            if (this.hasBank) location.href = 'tel:1600-0421';
            else $j.F.Mobile.Layout.goMobileMenu('1020');
        });

        // 운영시간 안내 — 접은 적이 없으면 펼쳐서 시작한다
        this._setNotice($j.Common.Store.local.get(LS_NOTICE_FOLDED) !== '1');
        $('#cxNoticeBtn').on('click', () => this.onClickNotice());

        Form.registerNumberHandler('cashoutamt');   // 천단위 콤마
        // 반환값을 흘리지 않는다 — validate() 의 false 가 jQuery 에 닿으면 전파가 끊겨 금액 말풍선($j.Ui.AmountKor)이 안 바뀐다
        this.$input.on('input', () => { this.validate(); });
        $('#myableamt').on('click', () => this.onClickAll());
        this.$submit.on('click', () => this.onClickSubmit());

        this.validate();
    }

    _setNotice(open) {
        $('#cxNoticeBtn').attr('aria-expanded', open ? 'true' : 'false');
        $('#cxNoticeBd').prop('hidden', !open);
    }

    /** 접으면 기억하고, 다시 펴면 기억을 지운다 — 마지막에 둔 상태가 다음 방문의 시작 상태다 */
    onClickNotice() {
        const open = $('#cxNoticeBtn').attr('aria-expanded') !== 'true';
        this._setNotice(open);
        if (open) $j.Common.Store.local.remove(LS_NOTICE_FOLDED);
        else $j.Common.Store.local.set(LS_NOTICE_FOLDED, '1');
    }

    amount() {
        return Util.int(String(this.$input.val()).replace(/,/g, ''));
    }

    /** 금액칸 상태를 판정해 안내문구·안내줄·버튼을 맞춘다. 막는 사유가 있으면 그 문장이 곧 안내줄이다. */
    validate() {
        const amount = this.amount();
        const won = (n) => Util.formatNumber(n) + '원';
        let msg = '', isError = false, blocked = false;

        if (!this.hasBank) { msg = '출금계좌를 먼저 등록해 주세요.'; isError = true; }
        else if (this.isPenalty) { msg = '환원건이 있어 출금할 수 없어요.'; isError = true; }
        else if (this.usable <= 0 || amount <= 0) blocked = true;   // 사유는 라벨 옆 안내문구가 이미 말한다
        else if (amount > this.usable) { msg = '출금가능금액 초과'; isError = true; }
        else if (amount < MIN_AMOUNT && amount !== this.usable) {
            msg = '1만원 이상 또는 전액만 가능'; isError = true;
        }

        // 라벨 옆 안내문구 — 얼마까지 낼 수 있는지는 늘 보인다
        $('#cxGuide').text(this.usable > 0
            ? `출금할 금액을 입력해 주세요. (최대 ${won(this.usable)})` : '출금할 수 있는 충전금이 없어요.');

        const ok = !msg && !blocked;
        this.$hint.text(ok ? `출금 후 잔여금 ${won(this.usable - amount)}` : msg).toggleClass('is-error', isError);
        this.$input.closest('.cx-input').toggleClass('is-error', isError && amount > 0);
        this.$submit.prop('disabled', !ok || this.busy);
        return ok;
    }

    onClickAll() {
        if (this.usable <= 0) { this.validate(); return; }
        this.$input.val(Util.formatNumber(this.usable));
        $j.Ui.AmountKor.refresh(this.$input[0]);
        this.validate();
    }

    async onClickSubmit() {
        if (this.busy || !this.validate()) return;
        const amount = this.amount();

        // 확인창 없이 바로 신청한다 — 결과는 토스트로 알리고, 잘못 넣었으면 내역의 [신청취소]로 되돌린다.
        // 묻는 것은 하나뿐: 최근 5일 안에 같은 금액으로 신청해 둔 건이 있을 때 (연타·중복 신청 방지)
        this.busy = true;
        this.$submit.prop('disabled', true);
        let same;
        try { same = await $.get('/m/ajax/account/existSame', { _money: amount, status: 20 }); }
        catch (e) { this._failed('출금 신청에 실패했어요. 잠시 후 다시 시도해 주세요.'); return; }
        if (same.exist && !await $j.Ui.Dialog.confirm('같은 금액으로 신청한 건이 이미 있어요', {
            desc: '그래도 한 번 더 신청할까요?', confirm: '계속 신청', cancel: '취소',
        })) { this.busy = false; this.validate(); return; }

        this.withdraw(amount);
    }

    withdraw(amount) {
        $.post('/m/ajax/account/exchange', { amount })
            .done((data) => {
                if (!data.result) { this._failed(data.message || '출금 신청에 실패했어요.'); return; }
                this._moveBalance(amount);
                this.$input.val('');
                $j.Ui.Toast.show('출금 요청됐어요', { type: 'success', place: 'bottom' });
                this.loadHistory(true);
            })
            .fail(() => this._failed('출금 신청에 실패했어요. 잠시 후 다시 시도해 주세요.'))
            .always(() => { this.busy = false; this.validate(); });
    }

    /** 실패하면 화면 숫자가 서버와 어긋났을 수 있다 — 알리고 새로 받는다 */
    async _failed(msg) {
        await $j.Ui.Dialog.alert(msg);
        location.reload();
    }

    /** 출금신청중 금액이 amount 만큼 늘었다(취소면 음수) — 출금가능에서 빼고 홀드에 더한다 */
    _moveBalance(amount) {
        this.usable -= amount;
        this.hold += amount;
        $('#cxUsable').text(Util.formatNumber(this.usable));
        $('#cxHold').text(Util.formatNumber(this.hold));
    }

    /* ───────────────── 실명인증 ───────────────── */

    onClickAuth() {
        const type = 'real';
        const callbackUrl = Util.getHost() + `/m/plugin/inicis/callback/${type}`;
        $j.F.Inicis.auth({ successUrl: callbackUrl, failUrl: callbackUrl, type });
    }

    onInicisCallback(data) {
        if (data.result) location.reload();
        else $j.Ui.Dialog.alert(data.message);
    }

    /* ───────────────── 내역 ───────────────── */

    /** @param {boolean} reset true 면 처음부터 다시 받는다 */
    loadHistory(reset = false) {
        // 무한 스크롤의 onLoad 는 연달아 불릴 수 있다 — 로딩 중 재진입을 막는다.
        // (reset 은 신청·취소 뒤 강제 새로고침이라 항상 통과시킨다)
        if (!reset && this.loadingHist) return;
        if (reset) this.rows = [];
        this.loadingHist = true;
        this.$more.prop('disabled', true);
        $.post('/m/ajax/account/list', { type: 2, start: this.rows.length, length: PAGE_SIZE, month_sum: 1 })
            .done((res) => {
                this.rows = this.rows.concat(res.data || []);
                this.monthSums = res.monthSums || {};
                this.recordsTotal = Util.int(res.recordsTotal);
                this.renderHistory();
            })
            .always(() => { this.loadingHist = false; this.$more.prop('disabled', false); });
    }

    renderHistory() {
        this.$more.prop('hidden', this.rows.length >= this.recordsTotal);

        if (!this.rows.length) {
            this.$hist.html(
                '<div class="cx-card cx-empty">'
                + '<svg class="cx-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="3.5" width="16" height="17" rx="2.5"/><path d="M8 8.5h8M8 12.5h8M8 16.5h5"/></svg>'
                + '<p class="cx-empty-tt">아직 출금 내역이 없어요</p>'
                + '<p class="cx-empty-desc">출금을 신청하면 여기에 표시돼요.</p></div>'
            );
            return;
        }

        // 월마다 카드 하나. 목록이 최신순이라 같은 달은 이어서 온다
        const groups = [];
        this.rows.forEach((row) => {
            const ym = dayjs(row.request_date).format('YYYY-MM');
            const last = groups[groups.length - 1];
            if (last && last.ym === ym) last.rows.push(row);
            else groups.push({ ym, rows: [row] });
        });

        this.$hist.html(groups.map((g) => {
            const sum = Util.formatNumber(Util.int(this.monthSums[g.ym]));
            return `<div class="cx-card cx-hist">`
                + `<h3 class="cx-month">${dayjs(g.ym + '-01').format('YYYY년 M월')}`
                + `<span class="cx-month-sum"><i>합계</i> ${sum}원</span></h3>`
                + g.rows.map((row) => this._rowHtml(row)).join('')
                + `</div>`;
        }).join(''));
    }

    _rowHtml(row) {
        const [badgeClass, badgeText] = STATUS[row.status] || ['is-ready', Util.nvl(row.status, '')];
        const when = dayjs(row.request_date);
        const bank = Util.decodeMemberBank(row.r_bank);
        // 내역에는 은행·예금주만 — 계좌번호는 적지 않는다
        const account = this._esc([bank.bank, bank.actholder].filter(Boolean).join(' '));
        const cancel = row.status == 20
            ? `<button type="button" class="cx-cancel" data-num="${Util.int(row.num)}">신청취소</button>` : '';

        return `<div class="cx-row${row.status == 23 ? ' is-cancel' : ''}">`
            + `<div class="cx-row-mid"><span class="cx-when">${when.format('M.D')} <em>${when.format('HH:mm')}</em></span>`
            + (account ? `<span class="cx-who-line">${account}</span>` : '') + `</div>`
            // [신청취소]는 뱃지 왼쪽에 둔다 — 왼쪽 칸 아래에 달면 그 행만 키가 커져 줄이 어긋난다
            + `<div class="cx-row-right"><span class="cx-amt">-${Util.formatNumber(Util.int(row.amount))}원</span>`
            + `<span class="cx-row-state">${cancel}<span class="cx-badge ${badgeClass}">${badgeText}</span></span></div></div>`;
    }

    _esc(str) {
        return $('<i>').text(str).html();
    }

    async onClickCancel(num) {
        const row = this.rows.find((r) => r.num == num);
        if (!row) return;

        const ok = await $j.Ui.Dialog.confirm('출금을 취소할까요?', {
            rows: [{ k: '취소 금액', v: Util.formatNumber(Util.int(row.amount)) + '원' }],
            confirm: '취소하기', cancel: '닫기',
        });
        if (!ok) return;

        $.ajax({ type: 'delete', url: '/m/ajax/account/cancel', data: { num } })
            .done((data) => {
                if (!data.result) { this._failed('취소하지 못했어요. 이미 처리 중인 건일 수 있어요.'); return; }
                this._moveBalance(-Util.int(row.amount));
                if (this.$input) this.validate();
                $j.Ui.Toast.show('출금을 취소했어요', { type: 'success', place: 'bottom' });
                this.loadHistory(true);
            })
            .fail(() => this._failed('취소하지 못했어요. 잠시 후 다시 시도해 주세요.'));
    }
}
